import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  ChevronDown,
  ChevronRight,
  Layers,
  Lock,
  Sparkles,
  PencilLine,
  Info,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  parseSnapshot,
  buildSnapshotFromBooking,
  type NightlySnapshot,
  type SnapshotMode,
  type NightComponent,
} from '@/lib/booking/nightlySnapshot';
import { BookingWithFinancials } from '@/hooks/useBookingCore';
import { useAtomicBookingSave } from '@/hooks/useAtomicBookingSave';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Zap } from 'lucide-react';

interface NightlyBreakdownPanelProps {
  booking: BookingWithFinancials;
  /** Колбэк после успешной смены режима (refresh брони) */
  onUpdate?: () => void | Promise<void>;
}

const MODE_LABELS: Record<SnapshotMode | 'null' | 'legacy_locked', {
  label: string;
  desc: string;
  icon: React.ReactNode;
  variant: 'default' | 'secondary' | 'outline';
}> = {
  auto: {
    label: 'Авто',
    desc: 'Старые ночи зафиксированы, новые ночи получают цену из тарифа автоматически',
    icon: <Sparkles className="h-3.5 w-3.5" />,
    variant: 'default',
  },
  manual: {
    label: 'Ручной',
    desc: 'Каждая ночь и каждый компонент редактируются вручную',
    icon: <PencilLine className="h-3.5 w-3.5" />,
    variant: 'secondary',
  },
  locked: {
    label: 'Snapshot Locked',
    desc: 'Покомпонентный snapshot заморожен',
    icon: <Lock className="h-3.5 w-3.5" />,
    variant: 'outline',
  },
  legacy_locked: {
    label: 'Зафиксированная сумма',
    desc: 'Используется ручная общая сумма (manual_total_amount)',
    icon: <Lock className="h-3.5 w-3.5" />,
    variant: 'outline',
  },
  null: {
    label: 'Классический',
    desc: 'Цена считается по текущим тарифам get_rate (как и раньше для всех броней)',
    icon: <Layers className="h-3.5 w-3.5" />,
    variant: 'outline',
  },
};

const KIND_LABELS: Record<NightComponent['kind'], string> = {
  lodging: 'Проживание',
  meal: 'Питание',
  extra_bed: 'Доп. место',
  crib: 'Детская кроватка',
  child_discount: 'Скидка на ребёнка',
  discount: 'Скидка',
  surcharge: 'Доплата',
};

const MEAL_LABELS: Record<string, string> = {
  breakfast: 'Завтрак',
  lunch: 'Обед',
  dinner: 'Ужин',
};

function formatRub(value: number | null | undefined) {
  const v = Number(value || 0);
  return `${v.toLocaleString('ru-RU')} ₽`;
}

export const NightlyBreakdownPanel: React.FC<NightlyBreakdownPanelProps> = ({
  booking,
  onUpdate,
}) => {
  const [open, setOpen] = useState(false);
  const [expandedNights, setExpandedNights] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const { saveBookingAtomic } = useAtomicBookingSave();
  const { currentHotel } = useOptimizedHotel();
  const { toast } = useToast();

  const snapshot: NightlySnapshot | null = useMemo(
    () => parseSnapshot((booking as any).nightly_snapshot),
    [booking]
  );

  const priceLocked = (booking as any).price_locked === true;
  const snapshotMode = (booking as any).snapshot_mode as SnapshotMode | null | undefined;
  const snapshotVersion = (booking as any).snapshot_version ?? 0;

  // Определяем эффективный режим
  const effectiveMode: keyof typeof MODE_LABELS = snapshot
    ? (snapshotMode || 'auto')
    : priceLocked
      ? 'legacy_locked'
      : 'null';

  const modeInfo = MODE_LABELS[effectiveMode];

  const toggleNight = (date: string) => {
    setExpandedNights((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  /**
   * 🔄 Смена режима (auto / manual / locked)
   * Если snapshot ещё нет — нельзя переключить (нужно создать через виджет/при создании брони).
   */
  const handleModeChange = async (newMode: SnapshotMode) => {
    if (!snapshot) {
      toast({
        variant: 'destructive',
        title: 'Snapshot ещё не создан',
        description:
          'Покомпонентный расчёт появится автоматически при следующем создании брони. Существующая бронь продолжает работать в классическом режиме.',
      });
      return;
    }
    if (newMode === snapshotMode) return;
    const hotelId = currentHotel?.id || (booking as any).hotel_id;
    if (!hotelId) return;

    setIsSaving(true);
    try {
      const result = await saveBookingAtomic(
        booking.id,
        hotelId,
        {
          snapshot_mode: newMode,
          expected_snapshot_version: snapshotVersion,
        },
        `Смена режима цены: ${snapshotMode || 'auto'} → ${newMode}`,
      );
      if (result && onUpdate) {
        await Promise.resolve(onUpdate());
      }
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * 🚀 Активация покомпонентного расчёта для legacy-брони.
   * Строит snapshot из текущих check_in/check_out + room + rate_plan через get_rates_matrix
   * и сохраняет через save_booking_atomic с mode='auto'.
   */
  const handleActivateComponentMode = async () => {
    const hotelId = currentHotel?.id || (booking as any).hotel_id;
    if (!hotelId) return;
    setIsActivating(true);
    try {
      const snap = await buildSnapshotFromBooking({
        supabase,
        hotelId,
        booking: {
          check_in_date: booking.check_in_date,
          check_out_date: booking.check_out_date,
          rate_plan_id: (booking as any).rate_plan_id ?? null,
          room_id: (booking as any).room_id ?? null,
          room_type: (booking as any).room_type ?? null,
          adult_count: (booking as any).adult_count ?? null,
          children_count: (booking as any).children_count ?? null,
          children_ages: (booking as any).children_ages ?? null,
          number_of_guests: (booking as any).number_of_guests ?? null,
        },
      });

      const result = await saveBookingAtomic(
        booking.id,
        hotelId,
        {
          nightly_snapshot: snap,
          snapshot_mode: 'auto',
          expected_snapshot_version: snapshotVersion,
        },
        'Активация покомпонентного расчёта (AK-47 v8)',
      );
      if (result) {
        toast({
          title: 'Покомпонентный режим включён',
          description: `Создан snapshot из ${snap.nights.length} ночей. Итого: ${snap.totals.grand_total.toLocaleString('ru-RU')} ₽`,
        });
        if (onUpdate) await Promise.resolve(onUpdate());
      }
    } catch (e: any) {
      console.error('[NightlyBreakdownPanel] activate failed:', e);
      toast({
        variant: 'destructive',
        title: 'Не удалось включить покомпонентный расчёт',
        description: e?.message || 'Проверьте, что у брони задан тариф и категория.',
      });
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Покомпонентный расчёт по ночам
          </span>
          <div className="flex items-center gap-2">
            <Badge variant={modeInfo.variant} className="flex items-center gap-1.5">
              {modeInfo.icon}
              {modeInfo.label}
            </Badge>
            {snapshot && (
              <Select
                value={snapshotMode || 'auto'}
                onValueChange={(v) => handleModeChange(v as SnapshotMode)}
                disabled={isSaving}
              >
                <SelectTrigger className="h-8 w-[160px] text-xs">
                  {isSaving ? (
                    <span className="flex items-center gap-1.5">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Сохранение…
                    </span>
                  ) : (
                    <SelectValue placeholder="Режим" />
                  )}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">
                    <span className="flex items-center gap-2">
                      <Sparkles className="h-3.5 w-3.5" /> Авто
                    </span>
                  </SelectItem>
                  <SelectItem value="manual">
                    <span className="flex items-center gap-2">
                      <PencilLine className="h-3.5 w-3.5" /> Ручной
                    </span>
                  </SelectItem>
                  <SelectItem value="locked">
                    <span className="flex items-center gap-2">
                      <Lock className="h-3.5 w-3.5" /> Зафиксирован
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground flex items-start gap-2">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
          {modeInfo.desc}
        </p>

        {!snapshot && (
          <div className="rounded-lg border border-dashed bg-muted/40 p-4 text-sm text-muted-foreground">
            Для этого бронирования покомпонентный snapshot ещё не создан. Текущая бронь
            продолжает работать в классическом режиме — данные не изменятся.
            {priceLocked && (
              <div className="mt-2 text-foreground">
                💡 Сумма зафиксирована вручную:{' '}
                <strong>{formatRub((booking as any).manual_total_amount || booking.total_amount)}</strong>
              </div>
            )}
            <div className="mt-3">
              <Button
                onClick={handleActivateComponentMode}
                disabled={isActivating || priceLocked}
                size="sm"
                className="gap-2"
              >
                {isActivating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Создание snapshot…
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4" />
                    🔄 Включить покомпонентный расчёт (AUTO)
                  </>
                )}
              </Button>
              {priceLocked && (
                <p className="mt-2 text-xs">
                  Сначала снимите фиксацию ручной суммы, затем активируйте покомпонентный режим.
                </p>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                Создаст snapshot ночей через get_rates_matrix + питание из настроек отеля.
                Эта бронь — единственная, что изменится. Остальные продолжат работать в классическом режиме.
              </p>
            </div>
          </div>
        )}

        {snapshot && (
          <>
            {/* ⚠️ Подсказка + кнопка пересоздания, если snapshot пустой (например создан до фикса column name) */}
            {snapshot.totals.grand_total === 0 && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-destructive" />
                  <div className="flex-1">
                    <div className="font-medium text-foreground">
                      Snapshot создан, но все цены = 0 ₽
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Возможно, snapshot был создан до исправления (категория/тариф не подтянулись).
                      Нажмите «Пересоздать», чтобы заново посчитать цены через get_rates_matrix.
                    </div>
                    <Button
                      onClick={handleActivateComponentMode}
                      disabled={isActivating}
                      size="sm"
                      variant="outline"
                      className="mt-2 gap-2"
                    >
                      {isActivating ? (
                        <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Пересоздание…</>
                      ) : (
                        <><RefreshCw className="h-3.5 w-3.5" /> Пересоздать snapshot</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Сводка totals */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <div className="rounded-md bg-muted px-3 py-2">
                <div className="text-xs text-muted-foreground">Проживание</div>
                <div className="font-semibold">{formatRub(snapshot.totals.lodging)}</div>
              </div>
              <div className="rounded-md bg-muted px-3 py-2">
                <div className="text-xs text-muted-foreground">Питание</div>
                <div className="font-semibold">
                  {formatRub(
                    snapshot.totals.meals_breakfast +
                      snapshot.totals.meals_lunch +
                      snapshot.totals.meals_dinner
                  )}
                </div>
              </div>
              <div className="rounded-md bg-muted px-3 py-2">
                <div className="text-xs text-muted-foreground">Доп. услуги</div>
                <div className="font-semibold">{formatRub(snapshot.totals.extras)}</div>
              </div>
              <div className="rounded-md bg-primary/10 px-3 py-2 border border-primary/20">
                <div className="text-xs text-muted-foreground">Итого</div>
                <div className="font-bold text-primary">
                  {formatRub(snapshot.totals.grand_total)}
                </div>
              </div>
            </div>

            <Collapsible open={open} onOpenChange={setOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" size="sm" className="w-full justify-between">
                  <span className="flex items-center gap-2">
                    {open ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    Подробнее по ночам ({snapshot.nights.length})
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {snapshot.currency} · v{snapshot.v}
                  </span>
                </Button>
              </CollapsibleTrigger>

              <CollapsibleContent className="mt-3">
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead>Дата</TableHead>
                        <TableHead>Тариф</TableHead>
                        <TableHead className="text-center">Гости</TableHead>
                        <TableHead className="text-right">Сумма ночи</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {snapshot.nights.map((night) => {
                        const isOpen = expandedNights.has(night.date);
                        return (
                          <React.Fragment key={night.date}>
                            <TableRow
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => toggleNight(night.date)}
                            >
                              <TableCell>
                                {isOpen ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </TableCell>
                              <TableCell className="font-medium">
                                {new Date(night.date).toLocaleDateString('ru-RU', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                })}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-0.5">
                                  <span>{night.rate_plan.name || '—'}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {night.rate_source}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center text-sm">
                                {night.guests.adults} взр
                                {night.guests.children > 0 && ` + ${night.guests.children} дет`}
                              </TableCell>
                              <TableCell className="text-right font-semibold">
                                {formatRub(night.night_total)}
                              </TableCell>
                            </TableRow>

                            {isOpen && (
                              <TableRow className="bg-muted/30">
                                <TableCell colSpan={5} className="p-0">
                                  <div className="px-12 py-3 space-y-1">
                                    {night.components.length === 0 ? (
                                      <div className="text-sm text-muted-foreground italic">
                                        Нет компонентов
                                      </div>
                                    ) : (
                                      night.components.map((c, idx) => (
                                        <div
                                          key={`${night.date}-${idx}`}
                                          className="flex items-center justify-between text-sm py-1 border-b last:border-b-0 border-border/50"
                                        >
                                          <div className="flex items-center gap-2">
                                            <Badge variant="outline" className="font-mono text-xs">
                                              {c.txn_code}
                                            </Badge>
                                            <span>
                                              {KIND_LABELS[c.kind] || c.kind}
                                              {c.kind === 'meal' && c.meal_type && (
                                                <span className="text-muted-foreground">
                                                  {' '}
                                                  · {MEAL_LABELS[c.meal_type] || c.meal_type}
                                                </span>
                                              )}
                                              {c.qty > 1 && (
                                                <span className="text-muted-foreground">
                                                  {' '}
                                                  × {c.qty}
                                                </span>
                                              )}
                                            </span>
                                          </div>
                                          <span
                                            className={cn(
                                              'font-medium',
                                              c.amount < 0 && 'text-destructive'
                                            )}
                                          >
                                            {formatRub(c.amount)}
                                          </span>
                                        </div>
                                      ))
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </>
        )}
      </CardContent>
    </Card>
  );
};
